import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchProfile, loginUser, registerUser } from '../services/authService';
import { AUTH_TOKEN_KEY, clearWebAuthSession, isTokenExpired } from '../utils/authSession';

const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === 'true' || process.env.DEBUG_AUTH === 'true';
const ROLE_CONTEXT_REFRESH_EVENT = 'auth-roles-updated';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  const hasLoggedOutRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setAuthReady(true);
      return;
    }

    const persistedToken = localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (DEBUG_AUTH) {
      // eslint-disable-next-line no-console
      console.debug('[auth] hydrate token', { hasToken: Boolean(persistedToken) });
    }

    setToken(persistedToken || null);
    setAuthReady(true);
  }, []);

  useEffect(() => {
    if (!authReady) {
      return;
    }

    if (!token) {
      setUser(null);
      setIsLoadingAuth(false);
      return;
    }

    if (isTokenExpired(token)) {
      if (DEBUG_AUTH) {
        // eslint-disable-next-line no-console
        console.debug('[auth] token expired during validation');
      }
      clearWebAuthSession();
      setUser(null);
      setToken(null);
      setIsLoadingAuth(false);
      return;
    }

    let isMounted = true;
    setIsLoadingAuth(true);

    fetchProfile(token)
      .then((data) => {
        if (isMounted) {
          setUser(data.user);
        }
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        const status = Number(error?.status);
        if (status === 401) {
          clearWebAuthSession();
          setUser(null);
          setToken(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingAuth(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [authReady, token]);

  const persistAuth = useCallback((payload) => {
    const nextToken = payload?.token || '';
    setToken(nextToken || null);
    setUser(payload.user);

    if (!nextToken) {
      return;
    }

    localStorage.setItem(AUTH_TOKEN_KEY, nextToken);
    sessionStorage.setItem(AUTH_TOKEN_KEY, nextToken);
  }, []);

  const register = useCallback(
    async (credentials) => {
      const payload = await registerUser(credentials);
      if (payload.token) {
        persistAuth(payload);
      }

      return payload;
    },
    [persistAuth]
  );

  const login = useCallback(
    async (credentials) => {
      const payload = await loginUser(credentials);
      persistAuth(payload);
      return payload;
    },
    [persistAuth]
  );

  const logout = useCallback(() => {
    // Guard against repeated logout cascades from concurrent unauthorized checks.
    if (hasLoggedOutRef.current) {
      return;
    }

    hasLoggedOutRef.current = true;
    setUser(null);
    setToken(null);
    clearWebAuthSession();

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth-logout'));
    }

  }, []);


  useEffect(() => {
    if (token) {
      hasLoggedOutRef.current = false;
    }
  }, [token]);

  const updateUser = useCallback((nextUser) => {
    setUser(nextUser);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      return null;
    }

    const data = await fetchProfile(token);
    setUser(data.user || null);
    return data.user || null;
  }, [token]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleRolesUpdated = () => {
      if (!token) return;
      fetchProfile(token)
        .then((data) => {
          setUser(data.user);
        })
        .catch(() => {
          // no-op; regular auth flow will handle failures
        });
    };

    window.addEventListener(ROLE_CONTEXT_REFRESH_EVENT, handleRolesUpdated);
    return () => {
      window.removeEventListener(ROLE_CONTEXT_REFRESH_EVENT, handleRolesUpdated);
    };
  }, [token]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleSessionExpired = () => {
      if (DEBUG_AUTH) {
        // eslint-disable-next-line no-console
        console.debug('[auth] session expired event received');
      }
      logout();
    };

    window.addEventListener('auth-session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('auth-session-expired', handleSessionExpired);
    };
  }, [logout]);

  const value = useMemo(
    () => ({
      authReady,
      isLoadingAuth,
      login,
      logout,
      persistAuth,
      register,
      refreshUser,
      updateUser,
      token,
      user,
    }),
    [authReady, isLoadingAuth, login, logout, persistAuth, register, refreshUser, token, updateUser, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
